import { generatePath } from 'react-router-dom';
import { Input } from '@accure/ui';
import { isEqual } from 'date-fns';
import { ChevronRight, FolderOpen } from 'lucide-react';
import { z } from 'zod';

import { Link } from '@/components/ui/link';
import { AlertType, alertTypeMapping } from '@/entities/alert-type';
import { codec, useQueryParams } from '@/hooks/router/use-query-params';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useNow } from '@/hooks/useNow';
import { CreateTaskComment } from '@/task-manager/api/mutations/create-task-comment';
import { DeleteTaskComment } from '@/task-manager/api/mutations/delete-task-comment';
import { UpdateTaskComment } from '@/task-manager/api/mutations/update-task-comment';
import { AlertStatus } from '@/task-manager/entities/alert-status';
import { formatDistance } from '@/utils/format/date';

import { UpdateTask } from '../../api/mutations/update-task';
import { TaskCardType } from '../../entities/task-card-type';
import type { TaskDetails as TaskDetailsType } from '../../entities/task-details';
import { TaskPriority } from '../../entities/task-priority';
import { TaskStatus } from '../../entities/task-status';
import { TaskPriorityBadge } from '../task-priority-badge';

import { TaskActions } from './actions/task-actions';
import { TaskComments } from './comments/task-comments';
import { DocumentPreview } from './document-preview';
import { TaskAttribute } from './task-attribute';
import { TaskDataOnboardingDescription } from './task-data-onboarding-description';
import { TaskDate } from './updates/task-date';
import { TaskDescription } from './updates/task-description';
import { TaskEnum } from './updates/task-enum';
import { TaskFiles } from './updates/task-files';
import { TaskReadOnlyFrontend } from './updates/task-readonly-frontend';
import { TaskUpdateAssignee } from './updates/task-update-assignee';
import { TaskUpdateFrontend } from './updates/task-update-frontend';
import { TaskUpdateSubtitle } from './updates/task-update-subtitle';
import { TaskUpdateTitle } from './updates/task-update-title';

export type TaskDetailsProps = {
  task: TaskDetailsType;
  onTaskCommentCreate: (input: CreateTaskComment.Input) => Promise<void>;
  onTaskCommentUpdate: (input: UpdateTaskComment.Input) => Promise<void>;
  onTaskCommentDelete: (input: DeleteTaskComment.Input) => Promise<void>;
  onTaskUpdate: (input: UpdateTask.Input) => Promise<void>;
  onClose?: () => void;
};

export function TaskDetails({
  task,
  onTaskCommentCreate,
  onTaskCommentDelete,
  onTaskCommentUpdate,
  onTaskUpdate,
  onClose,
}: TaskDetailsProps) {
  const now = useNow(60_000);
  const featureFlag = useFeatureFlag();

  const [{ fileId }, queryParams] = useQueryParams({
    fileId: {
      key: 'fileId',
      schema: z.string().optional(),
      codec: codec.string,
    },
  });

  return (
    <div
      className="grid size-full grid-rows-[min-content_1fr]"
      data-testid="task-details"
    >
      <TaskActions task={task} onClose={onClose} />
      <div className="grid size-full grid-rows-[1fr_min-content] gap-6 px-8 py-6">
        <div className="space-y-8">
          {featureFlag('projectManagement') &&
          task.projectManagement?.project.id ? (
            <Link
              to={{
                pathname: generatePath('/asset/:assetId/project/workstreams', {
                  assetId: task.projectManagement.project.id,
                }),
                search: new URLSearchParams({
                  id: task.projectManagement.workstream.id,
                }).toString(),
              }}
            >
              <div className="-my-4 inline-flex items-center gap-2 rounded p-2 text-xs text-neutral-500 hover:bg-neutral-50 hover:text-neutral-600">
                {task.projectManagement.project.title}
                <ChevronRight className="size-3" />
                <span className="flex items-center gap-2">
                  <FolderOpen className="size-3" />
                  {task.projectManagement.workstream.title}
                </span>
                <ChevronRight className="size-3" />
              </div>
            </Link>
          ) : null}
          <div className="-mx-2 space-y-1">
            <TaskUpdateTitle
              title={task.title}
              required
              onChange={(title) => onTaskUpdate({ id: task.id, title })}
              className="!text-lg font-bold"
            />
            <TaskUpdateSubtitle
              task={task}
              onChange={(subtitle) => onTaskUpdate({ id: task.id, subtitle })}
            />
          </div>
          <div className="space-y-4">
            <TaskEnum<TaskStatus>
              label="Task Status"
              placeholder="Select a status"
              value={task.status}
              options={Object.values(TaskStatus)}
              onChange={(status) => onTaskUpdate({ id: task.id, status })}
            />

            <TaskEnum<TaskPriority>
              label="Priority"
              placeholder="Select a priority"
              value={task.priority}
              options={Object.values(TaskPriority)}
              renderOption={(priority) => (
                <TaskPriorityBadge priority={priority} />
              )}
              onChange={(priority) => onTaskUpdate({ id: task.id, priority })}
            />

            <TaskDate label="Due date" task={task} />

            <TaskUpdateAssignee task={task} />

            {task.cardType === TaskCardType.ALERT && task.alert ? (
              <>
                {task.alert.status ? (
                  <TaskEnum<AlertStatus>
                    label="Alert Status"
                    placeholder="Select a status"
                    value={task.alert.status}
                    options={Object.values(AlertStatus)}
                  />
                ) : null}
                {task.alert.type ? (
                  <TaskEnum<AlertType>
                    label="Alert Type"
                    placeholder="Select a type"
                    value={task.alert.type}
                    options={Object.values(AlertType)}
                    renderOption={(type) => alertTypeMapping[type]}
                  />
                ) : null}
              </>
            ) : null}

            {task.cardType === TaskCardType.STANDARD ? (
              <TaskUpdateFrontend task={task} />
            ) : null}

            {task.cardType === TaskCardType.ALERT ? (
              <TaskReadOnlyFrontend task={task} />
            ) : null}

            {task.device?.unitType ? (
              <TaskAttribute label="Device type">
                <Input disabled value={task.device.unitType} />
              </TaskAttribute>
            ) : null}

            {task.device?.cellType ? (
              <TaskAttribute label="Cell type">
                <Input disabled value={task.device.cellType} />
              </TaskAttribute>
            ) : null}

            <TaskAttribute label="Files">
              <TaskFiles task={task} />
            </TaskAttribute>
          </div>

          {task.cardType === TaskCardType.DATA_ONBOARDING_INCIDENT ? (
            <TaskDataOnboardingDescription
              description={task.description}
              customFields={task.customFields}
            />
          ) : (
            <TaskDescription
              description={task.description}
              onChange={(description) =>
                onTaskUpdate({ id: task.id, description })
              }
            />
          )}

          <TaskComments
            comments={task.comments}
            onCreate={(input) =>
              onTaskCommentCreate({ ...input, taskId: task.id })
            }
            onUpdate={(input) =>
              onTaskCommentUpdate({ ...input, taskId: task.id })
            }
            onDelete={(input) =>
              onTaskCommentDelete({ ...input, taskId: task.id })
            }
          />
        </div>
        <div className="text-right text-xs text-gray-400">
          {!isEqual(task.modifiedAt, task.createdAt) ? (
            <p>Updated {formatDistance(now, task.modifiedAt)}</p>
          ) : null}
          <p>Created {formatDistance(now, task.createdAt)}</p>
        </div>
      </div>
      <DocumentPreview
        task={task}
        fileId={fileId}
        onClose={() => queryParams.replace({ fileId: undefined })}
      />
    </div>
  );
}
